import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';

const DocumentTitleContext = React.createContext<{
  title: string[];
  setIsOverridden: (isOverridden: boolean) => void;
}>({ title: ['Studio'], setIsOverridden: () => {} });

interface Props {
  children?: React.ReactNode;
  title: string | string[];
}

/**
 * Prepends `title` prop to all parent titles
 */
export const DocumentTitle = ({ children, title }: Props) => {
  const parentContext = useContext(DocumentTitleContext);
  const [overrides, setOverrides] = useState(0);
  const [fullTitle, setFullTitle] = useState(parentContext.title);
  useEffect(() => {
    setFullTitle((currentTitle) => {
      const nextTitle = [
        ...(Array.isArray(title) ? title : [title]),
        ...parentContext.title,
      ];
      if (_.isEqual(currentTitle, nextTitle)) {
        return currentTitle;
      } else {
        return nextTitle;
      }
    });
  }, [parentContext.title, title]);

  const documentTitleState = React.useMemo(
    () => ({
      title: fullTitle,
      setIsOverridden: (isOverriden: boolean) => {
        setOverrides((c) => (isOverriden ? c + 1 : c - 1));
      },
    }),
    [fullTitle],
  );

  // This `useEffect` cannot have any `deps`. There will often be many of these
  // rendered on the screen at once; specifying anything in `deps` will only
  // compare renders against _this instance_, not against siblings.
  useEffect(() => {
    if (overrides <= 0) {
      document.title = documentTitleState.title.join(' | ');
    }
  });

  useEffect(() => {
    parentContext.setIsOverridden(true);
    return () => parentContext.setIsOverridden(false);
  }, [parentContext]);

  if (children) {
    // If we have children then wrap them in the context provider so they can
    // access the title via `DocumentTitleContext.Consumer`. This will allow for
    // progressive title updates.
    return (
      <DocumentTitleContext.Provider value={documentTitleState}>
        {children}
      </DocumentTitleContext.Provider>
    );
  }

  return null;
};
